/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================
* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim 
Coded by www.creative-tim.com
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable/ServerSidePagination";

import {
  Card,
  CircularProgress,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import MDBadge from "components/MDBadge";
import { useMaterialUIController } from "context";
import moment from "moment/moment";
import MDButton from "components/MDButton";
import { GetAuditTrail, GetAuditTrailDownload } from "api-services";
import "./audit.css";
import { ShowErrorMessage, ShowSuccessMessage } from "helpers";
import { CSVLink } from "react-csv";
import MDPagination from "components/MDPagination";
import PropTypes from "prop-types";

const getToday = moment();
const getYesterday = moment().subtract(1, "day");

const Author = ({ name, email }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDBox lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {name}
      </MDTypography>
      <MDTypography variant="caption">{email}</MDTypography>
    </MDBox>
  </MDBox>
);

function Audit() {
  const filter = "";
  const [controller, dispatch] = useMaterialUIController();
  const today = getToday.format("L");
  const yesterday = getYesterday.format("L");
  const [dateFrom, setDateFrom] = useState(yesterday);
  const [dateTo, setDateTo] = useState(today);
  const [pageIndex, setPageIndex] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [downloadData, setDownloadData] = useState(null);

  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const userFilterFetch = window.location.hash.substring(1);

  const [rows, setRows] = useState([]);
  const { user_data } = useSelector((state) => state.authentication);
  const { darkMode } = controller;
  const [dataLoading, setDataLoading] = useState(true);
  const dispatchData = useDispatch();
  const determineAction = (filter) => {
    if (filter === "Login") return "Login";
    if (filter === "Approved") return "Approved";
    if (filter === "Pending") return "Pending";
    if (filter === "In Review") return "In Review";
    if (filter === "Rejected") return "Rejected";
    if (filter === "Suspended") return "Suspended";
    if (filter === "Resolved") return "Resolved";
    if (filter === "Unresolved") return "Unresolved";

    return ""; // Default case
  };

  const [action, setAction] = useState(determineAction(userFilterFetch));

  useEffect(() => {
    setDataLoading(true);
    GetAuditTrail(dispatchData, successAction, errorAction, {
      filter,
      action,
      dateFrom,
      dateTo,
      entriesPerPage,
      pageIndex,
    });
  }, [pageIndex, entriesPerPage]);

  // Download File
  useEffect(() => {
    // Update downloadData whenever action, dateFrom, or dateTo changes
    GetAuditTrailDownload(
      dispatchData,
      successActionDownload,
      errorActionDownload,
      action,
      dateFrom,
      dateTo
    );
  }, [action, dateFrom, dateTo, entriesPerPage]);

  const successActionDownload = (res) => {
    setDataLoading(false);
    setDownloadData(res?.data);
  };

  const errorActionDownload = (err) => {
    setDataLoading(false);
    console.log(err);
  };

  const DownloadAT = () => {
    setDataLoading(true);
    setDownloadData(null);
    GetAuditTrailDownload(
      dispatchData,
      successActionDownload,
      errorActionDownload,
      action,
      dateFrom,
      dateTo
    );
    ShowSuccessMessage(dispatch, "Data exported successfully");
  };

  const columns = [
    { Header: "name", accessor: "name", align: "left" },
    { Header: "action", accessor: "action", align: "left" },
    { Header: "description", accessor: "description", align: "left" },
    { Header: "action on", accessor: "action_on", align: "left" },
    { Header: "status", accessor: "status", align: "left" },
    { Header: "timestamp", accessor: "time_stamp", align: "left" },
  ];

  const getBadgeColor = (action) => {
    switch (action) {
      case "In Review":
      case "Login":
        return "info";
      case "Approved":
      case "Resolved":
        return "success";
      case "Pending":
        return "warning";
      case "Rejected":
      case "Unresolved":
        return "error";
      default:
        return "error"; // Fallback color
    }
  };

  const handleEntriesChange = (newEntries) => {
    setEntriesPerPage(newEntries); // Pass the newEntries to another component if needed
  };

  const successAction = (res) => {
    setDataLoading(false);

    const allData = res.data.data.map((item) => {
      setTotalPages(res.data.totalCount);

      return {
        name: <Author name={item.userName} email={item.email} />,
        action: (
          <MDBox sx={{ cursor: "pointer" }} ml={-1}>
            <MDBadge
              badgeContent={item.action}
              color={getBadgeColor(item.action)}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        description: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.description || "N/A"}
          </MDTypography>
        ),
        action_on: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item.performedOn || "N/A"}
          </MDTypography>
        ),
        status: (
          <MDBox sx={{ cursor: "pointer" }} ml={-1}>
            <MDBadge
              badgeContent={item.status}
              color={item.status === "Success" ? "success" : "error"}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        time_stamp: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {moment(item.date).format("LLL")}
          </MDTypography>
        ),
      };
    });

    setRows(allData);
  };

  const errorAction = () => {
    setDataLoading(false);
  };

  const handleActionStatus = (action) => {
    setPageIndex(0);
    setDataLoading(true);
    setAction(action);
    GetAuditTrail(dispatchData, successAction, errorAction, {
      filter,
      action,
      dateFrom,
      dateTo,
      entriesPerPage,
      pageIndex,
    });
  };

  const handleDateRangeOne = (dateFrom) => {
    setPageIndex(0);
    errorAction();
    if (moment(dateFrom).isAfter(today)) {
      ShowErrorMessage(
        dispatch,
        "Please select valid Date.",
        "Please select valid Date."
      );
    } else {
      setDataLoading(true);
      setDateFrom(dateFrom);
      GetAuditTrail(dispatchData, successAction, errorAction, {
        filter,
        action,
        dateFrom,
        dateTo,
        entriesPerPage,
        pageIndex,
      });
    }
  };

  const handleDateRangeTwo = (dateTo) => {
    setPageIndex(0);
    errorAction();

    // Check if the end date is greater than or equal to start date
    if (moment(dateTo).isAfter(today)) {
      ShowErrorMessage(dispatch, "You cannot choose a future date.", "");
    } else if (moment(dateTo).isBefore(moment(dateFrom))) {
      ShowErrorMessage(
        dispatch,
        "Please ensure that the End Date is greater than the Start Date.",
        ""
      );
    } else {
      setDataLoading(true);
      setDateTo(dateTo);
      GetAuditTrail(dispatchData, successAction, errorAction, {
        filter,
        action,
        dateFrom,
        dateTo,
        entriesPerPage,
        pageIndex,
      });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setPageIndex(currentPage - 1);
    } else if (currentPage === 1) {
      setCurrentPage(0);
      setPageIndex(0);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setPageIndex(currentPage + 1);
    }
  };

  const entriesStart =
    pageIndex === 0 ? pageIndex + 1 : pageIndex * entriesPerPage + 1;

  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = entriesPerPage;
  } else if (pageIndex === currentPage.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = entriesPerPage * (pageIndex + 1);
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className={darkMode ? "darkmode_wrap" : "lightmode_wrap"}>
        <MDBox mb={3} mt={2} className="audit-trail">
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <Grid
                  container
                  spacing={5}
                  px={2}
                  py={0}
                  sx={{ placeItems: "flex-end" }}
                >
                  <Grid item xs={12} xl={2} mt={2}>
                    <TextField
                      type="date"
                      className="form-control"
                      id="dateFrom"
                      name="dateFrom"
                      label="Start Date"
                      value={dateFrom}
                      defaultValue={dateFrom}
                      onChange={(e) => {
                        handleDateRangeOne(e.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={2} mt={2}>
                    <TextField
                      type="date"
                      className="form-control"
                      id="dateTo"
                      name="dateTo"
                      label="End Date"
                      max={today}
                      value={dateTo}
                      defaultValue={dateTo}
                      onChange={(e) => {
                        handleDateRangeTwo(e.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={2}>
                    <FormControl fullWidth>
                      <InputLabel
                        id="demo-simple-select-label"
                        variant="outlined"
                        color="success"
                        className="inputbox mb-2"
                      >
                        Actions
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={action}
                        label="User Status"
                        onChange={(e) => {
                          handleActionStatus(e.target.value);
                        }}
                        sx={{ height: "45px" }}
                      >
                        <MenuItem value={"Login"}>Login</MenuItem>
                        <MenuItem value={"Approved"}>Approved</MenuItem>
                        <MenuItem value={"Pending"}>Pending</MenuItem>
                        <MenuItem value={"In Review"}>In Review</MenuItem>
                        <MenuItem value={"Rejected"}>Rejected</MenuItem>
                        <MenuItem value={"Suspended"}>Suspended</MenuItem>
                        <MenuItem value={"Resolved"}>Resolved</MenuItem>
                        <MenuItem value={"Unresolved"}>Unresolved</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    xl={4}
                    p={0}
                    className={
                      dataLoading === false ? "padd-zero audit-t" : "padd-zero"
                    }
                  ></Grid>

                  <Grid
                    item
                    xs={12}
                    xl={2}
                    sx={{ justifyContent: "end", display: "flex" }}
                  >
                    <MDBox mr={1}>
                      {downloadData && (
                        <CSVLink
                          data={downloadData}
                          className="white"
                          asyncOnClick={true}
                          filename={`Audit Trail ${dateFrom} - ${dateTo}.csv`}
                        >
                          <MDButton
                            className="btn-spc"
                            color="success"
                            onClick={DownloadAT}
                          >
                            {downloadData ? (
                              <span>Download Report</span>
                            ) : (
                              <CircularProgress color="white" size={22} />
                            )}
                          </MDButton>
                        </CSVLink>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
                <MDBox
                  pt={3}
                  pb={3}
                  display="flex"
                  justifyContent="center"
                  className={
                    dataLoading === false ? "padd-zero audit-wrap" : "padd-zero"
                  }
                >
                  {dataLoading ? (
                    <CircularProgress color="success" size={30} />
                  ) : (
                    <>
                      <DataTable
                        table={{ columns, rows }}
                        isSorted={true}
                        // entriesPerPage={true}
                        entriesPerPage={{
                          defaultValue: entriesPerPage,
                          entries: [5, 10, 15, 20, 25],
                        }}
                        onEntriesChange={handleEntriesChange}
                        showTotalEntries={true}
                        canSearch={true}
                        noEndBorder={false}
                        manualPagination={true}
                        setGlobalFilterAPI={(filter) => {
                          GetAuditTrail(
                            dispatchData,
                            successAction,
                            errorAction,
                            {
                              filter,
                              action,
                              dateFrom,
                              dateTo,
                              entriesPerPage,
                              pageIndex,
                            }
                          );
                        }}
                      />
                      <MDBox
                        pt={3}
                        pb={3}
                        className="padd-zero audit-pagination"
                      >
                        <MDBox mb={{ xs: 3, sm: 0 }}>
                          <MDTypography
                            variant="button"
                            color="secondary"
                            fontWeight="regular"
                          >
                            Showing {entriesStart} {"to "}
                            {rows.length < entriesEnd ? totalPages : entriesEnd}
                            {" of "} {totalPages} entries
                          </MDTypography>
                        </MDBox>
                        <MDBox mb={{ xs: 3, sm: 0 }}>
                          <MDPagination
                            item
                            onClick={handlePrevPage}
                            disabled={totalPages <= 9}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_left
                            </Icon>
                          </MDPagination>
                          <MDPagination
                            item
                            onClick={handleNextPage}
                            disabled={
                              currentPage === totalPages || totalPages <= 9
                            }
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_right
                            </Icon>
                          </MDPagination>
                        </MDBox>
                      </MDBox>
                    </>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </div>
      <Footer />
    </DashboardLayout>
  );
}

// Define prop types
Author.propTypes = {
  name: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

export default Audit;
